import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { User } from "../model/users";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Labeler } from "../model/labeler";
import { UserPass } from "../model/user-pass";

import { PALETTE } from "../model/palette";

@Injectable({
  providedIn: "root",
})
export class UsersService implements Labeler {
  private apiUrl: string;
  private byIdUrl: string;
  public _currentUsers: BehaviorSubject<User[]>;
  public _currentUser: BehaviorSubject<User>;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private env: EnvironmentHelper,
    private router: Router
  ) {
    this.apiUrl = `${env.apiBaseUrl}user/`;
    this.byIdUrl = `${env.apiBaseUrl}user/by-id/`;
    this._currentUsers = new BehaviorSubject<User[]>([]);
    this._currentUser = new BehaviorSubject<User>(User.EMPTY);
  }

  public users(token: string): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(this.apiUrl, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receiveUsers(response, observer);
          },
          (error) => {
            this.receiveUsers(error, observer);
          }
        );
    });
  }

  public createUser(token: string, user: User): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(this.apiUrl, user, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveUser(response, observer),
          (error) => {
            this.receiveUser(error, observer);
          }
        );
    });
  }

  public updateUser(token: string, user: User): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${user.id}`;

    return new Observable<Messages>((observer) => {
      if (!user.id) {
        observer.next(Messages.from({ users: ["users.id.required"] }));
        return;
      }

      this.http
        .put<any>(url, user, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receiveUser(response, observer);
          },
          (error) => {
            this.receiveUser(error, observer);
          }
        );
    });
  }

  public deleteUser(token: string, userId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${userId}`;

    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveUser(response, observer),
          (error) => this.receiveUser(error, observer)
        );
    });
  }
  public changePassword(token: string, user: UserPass): Observable<Messages> {
    console.trace("usersService changeOassword ");
    const url = `${this.apiUrl}password`;

    return new Observable<Messages>((observer) => {
      this.http
        .put<any>(url, user, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivePasswordChange(response, observer),
          (error) => this.receivePasswordChange(error, observer)
        );
    });
  }

  private receivePasswordChange(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 204) {
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(Messages.from((response as HttpErrorResponse).error));
    } else if (
      response.status === 409 ||
      response.status === 400 ||
      response.status === 500
    ) {
      observer.next(Messages.from((response as HttpErrorResponse)?.error));
    } else {
      observer.next(
        Messages.from({
          user: [
            "user.technical.error " + response.status,
            (response as HttpErrorResponse)?.error as string,
          ],
        })
      );
    }
  }

  public getUserById(token: string, userId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${userId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveUser(response, observer),
          (error) => this.receiveUser(error, observer)
        );
    });
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const userId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getUserById(token, Number(userId)).subscribe((messages) => {});
    } else {
      const emptyUser = new User(0, "", "", "", true, false);
      this.select(emptyUser);
    }
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  private receiveUsers(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<User[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ users: ["users.empty"] }));
        return;
      }
      this._currentUsers.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 204) {
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "users",
          "users.incomplete"
        )
      );
    }
  }

  private receiveUser(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<User>)?.body;
      if (!data) {
        observer.next(Messages.from({ user: ["user.unknown"] }));
        return;
      }
      this._currentUser.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 204) {
      observer.next(Messages.EMPTY);
    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "user",
          "user.load.error."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  public select(user: User) {
    this._currentUser.next(user);
  }

  public get usersObservable(): Observable<User[]> {
    return this._currentUsers.asObservable();
  }
  public get userObservable(): Observable<User> {
    return this._currentUser.asObservable();
  }

  public get usersData(): User[] {
    return this._currentUsers.value;
  }

  public get selected(): User | null {
    return this._currentUser.value;
  }
  public labelForId(id: number): string {
    let label = "-";
    if (this.usersData?.length > 0) {
      const found = this.usersData.filter((users) => id === users.id);
      if (found.length > 0) {
        label = found[0].name;
      }
    }
    return label;
  }
  public makeLabel(id: number): Observable<string> {
    const observable = new BehaviorSubject("");

    if (this.usersData?.length > 0) {
      const label = this.labelForId(id);
      observable.next(label);
    } else {
      this.users(this.auth.token).subscribe((m) => {
        observable.next(this.labelForId(id));
      });
    }
    return observable;
  }
  public makeColor(id: number): Observable<string> {
    return new BehaviorSubject(PALETTE.ink);
  }
  /** The colour of the user category: zeeblauw (#173). */
  public makeBackgroundColor(id: number): Observable<string> {
    return new BehaviorSubject(PALETTE.zeeblauw);
  }
  public makeImportance(id: number): Observable<number> {
    return new BehaviorSubject(1);
  }
  public getUserIdByUsername(username: string): number | null {
    const found = this.usersData.find((user) => user.name === username);
    return found?.id ?? null;
  }
  public getUserByUsername(username: string): User | null {
    const found = this.usersData.find((user) => user.name === username);
    return found ?? null;
  }
}
